import TimeWindow from './timeWindow'
import { DateTime } from 'luxon'

export default class ScheduleGrid {
  /**
   * A agenda recebe horário em 'H:mm' quando vem do GET, mas em 'HH:mm:ss' em alguns
   * caminhos (resposta de PUT e payload de socket devolvem a string crua que o cliente
   * mandou). DateTime.fromFormat não lança para um formato que não bate -- devolve um
   * DateTime inválido, e toda comparação contra ele vira false em silêncio. Aceitar as
   * duas formas aqui é o que evita esse tipo de sumiço.
   */
  static parseTime(time) {
    if (typeof time !== 'string') return DateTime.invalid('horário ausente')
    return DateTime.fromFormat(time, time.length > 5 ? 'HH:mm:ss' : 'H:mm')
  }

  static formatTime(time) {
    return time.toFormat('H:mm')
  }

  /** Reserva de agenda não tem `validated`; é o único marcador que a separa de um agendamento. */
  static isReservation(schedule) {
    return schedule.validated === undefined
  }

  /**
   * Início e fim de qualquer item da grade. Reserva carrega start_time/end_time
   * explícitos; agendamento carrega `time` e `duration`, e o fim é derivado.
   */
  static getScheduleRange(schedule) {
    const start = ScheduleGrid.parseTime(
      ScheduleGrid.isReservation(schedule) ? schedule.start_time : schedule.time
    )

    const end = ScheduleGrid.isReservation(schedule)
      ? ScheduleGrid.parseTime(schedule.end_time)
      : start.plus({ minutes: Number(schedule.duration) || 0 })

    return { start, end }
  }

  /** Um agendamento é dado como concluído depois do horário mais a tolerância de atraso. */
  static isCompleted(schedule) {
    const scheduleDateTime = DateTime.fromFormat(
      `${schedule.date} ${schedule.time}`, 'yyyy-MM-dd H:mm'
    )

    if (!scheduleDateTime.isValid) return false

    return DateTime.now() >= scheduleDateTime.plus({ minutes: Number(schedule.max_delay) + 1 })
  }

  static isMissed(schedule) {
    return schedule.cutilei_schedule
      ? ScheduleGrid.isCompleted(schedule) && !schedule.validated
      : schedule.status === 'missed'
  }

  /** Estende a duração até fechar na próxima janela da grade. */
  static roundUpToWindow(duration, timeInterval) {
    if (!timeInterval) return duration
    return duration + ((timeInterval - duration % timeInterval) % timeInterval)
  }

  /**
   * Faixa que o bloco realmente ocupa na grade, que difere da faixa real em dois pontos:
   * um agendamento perdido é encolhido para uma janela, e a duração é arredondada para
   * cima até fechar na próxima janela.
   *
   * Tem que ser uma função só. Enquanto a altura do bloco e a contagem de sobreposição
   * calculavam isso cada uma do seu jeito, as duas discordavam -- a posição dizia um
   * horário e a largura dizia outro.
   */
  static getRenderedRange(schedule, timeInterval) {
    const { start, end } = ScheduleGrid.getScheduleRange(schedule)

    if (ScheduleGrid.isReservation(schedule) || !start.isValid) return { start, end }

    const duration = Number(schedule.duration) || 0
    const renderedDuration = ScheduleGrid.isMissed(schedule) && duration > timeInterval
      ? timeInterval
      : ScheduleGrid.roundUpToWindow(duration, timeInterval)

    return { start, end: start.plus({ minutes: renderedDuration }) }
  }

  static getProfessionalTimeWindows = (professional, timeWindows, timeInterval) => {
      const start = timeWindows[0]
      const end = timeWindows[timeWindows.length - 1]
      const interval = professional.time_interval || timeInterval
  
      return TimeWindow.getTimeWindows (start, end, false, interval).map (tw => tw.value)
    }

  /**
   * Lista de janelas do dia, do primeiro ao último horário que a agenda precisa mostrar.
   *
   * A grade nasce do horário de funcionamento, mas qualquer item que caia fora dele a
   * estica. Reservas entram nessa conta junto com os agendamentos: enquanto ficavam de
   * fora, uma reserva inteiramente antes da abertura do salão não ganhava linha própria,
   * o bloco caía na primeira linha da grade e desenhava com a altura de uma só janela.
   */
  static getDayTimeWindows({ schedules = [], opening, closing, timeInterval }) {
    const parsedOpening = ScheduleGrid.parseTime(opening)
    const parsedClosing = ScheduleGrid.parseTime(closing)

    if (!parsedOpening.isValid || !parsedClosing.isValid || !timeInterval) return []

    const scheduleRanges = schedules
      .map(schedule => ScheduleGrid.getRenderedRange(schedule, timeInterval))
      .filter(({ start, end }) => start.isValid && end.isValid)

    const earliest = scheduleRanges.reduce(
      (earliest, { start }) => start < earliest ? start : earliest, parsedOpening
    )

    const latest = scheduleRanges.reduce(
      (latest, { end }) => end > latest ? end : latest, parsedClosing
    )

    // A grade é ancorada na abertura: um item fora dela estica o intervalo até a fronteira
    // de janela mais próxima. Sem isso, um item às 10:47 deslocaria em 13 minutos todas as
    // janelas do dia, e nenhum horário redondo casaria mais com a lista.
    const snapToWindow = (time, round) => parsedOpening.plus({
      minutes: round(time.diff(parsedOpening, 'minutes').minutes / timeInterval) * timeInterval
    })

    return TimeWindow.getTimeWindows(
      ScheduleGrid.formatTime(snapToWindow(earliest, Math.floor)),
      ScheduleGrid.formatTime(snapToWindow(latest, Math.ceil)),
      false,
      timeInterval
    ).map(timeWindow => timeWindow.value)
  }

  /**
   * Índice da linha de `time` dentro da lista de janelas da coluna.
   *
   * O indexOf exato continua sendo o caminho normal. A aritmética existe para horário
   * que não está na lista -- uma reserva que começa antes da abertura do salão, por
   * exemplo. Antes isso virava -1, o bloco caía na primeira linha da grade e o
   * grid-row-end virava 0 (linha inexistente em CSS Grid), então o browser descartava a
   * declaração e o bloco desenhava com uma linha de altura, independente da duração real.
   *
   * Devolve null quando não dá para calcular; quem chama decide o fallback.
   */
  static getRowIndex(timeWindows, time, timeInterval) {
    const exactIndex = timeWindows.indexOf(time)
    if (exactIndex >= 0) return exactIndex

    const firstWindow = ScheduleGrid.parseTime(timeWindows[0])
    const parsedTime = ScheduleGrid.parseTime(time)

    if (!firstWindow.isValid || !parsedTime.isValid || !timeInterval) return null

    return Math.round(parsedTime.diff(firstWindow, 'minutes').minutes / timeInterval)
  }

  /**
   * Maior número de itens que se sobrepõem numa mesma janela da coluna. Define quantas
   * sub-colunas a coluna do profissional tem -- é o que produz a divisão 50/50 de dois
   * itens marcados no mesmo horário.
   */
  static getNumConflictingSchedules(schedules, timeWindows, timeInterval) {
    const scheduleRanges = schedules.map(schedule => (
      ScheduleGrid.getRenderedRange(schedule, timeInterval)
    ))

    const overlapsPerWindow = timeWindows.map(timeWindow => {
      const parsedTimeWindow = ScheduleGrid.parseTime(timeWindow)

      return scheduleRanges.filter(({ start, end }) => (
        parsedTimeWindow >= start && parsedTimeWindow < end
      )).length
    })

    // Math.max (...[]) é -Infinity, que vazaria para repeat(-Infinity, ...) no CSS.
    // O piso é 1: uma coluna sempre tem ao menos uma sub-coluna.
    return Math.max(1, ...overlapsPerWindow)
  }

  /**
   * Quantos outros itens da coluna se sobrepõem a este. Zero significa que o bloco pode
   * ocupar a largura inteira; qualquer valor maior o obriga a dividir o espaço.
   */
  static getOverlappingSchedules(schedules, schedule, timeInterval) {
    const { start, end } = ScheduleGrid.getRenderedRange(schedule, timeInterval)

    if (!start.isValid || !end.isValid) return 0

    return schedules.filter(otherSchedule => {
      if (otherSchedule.id === schedule.id) return false

      const other = ScheduleGrid.getRenderedRange(otherSchedule, timeInterval)

      return other.start.isValid && other.end.isValid
        && start < other.end && end > other.start
    }).length
  }
}
